import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Session } from '../types';

interface TodaySummaryProps {
  sessions: Session[];
}

export const TodaySummary: React.FC<TodaySummaryProps> = ({ sessions }) => {
  const total = sessions.length;
  const productive = sessions.filter(s => s.productive).length;
  const unproductive = total - productive;
  const rate = total > 0 ? Math.round((productive / total) * 100) : 0;
  const totalMinutes = sessions.reduce((acc, s) => acc + s.durationMinutes, 0);

  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-6">
      <StatCard label="Completed Intervals" value={total} />
      <StatCard label="Productivity Rate" value={`${rate}%`} highlight={rate >= 50 ? 'text-yes' : rate > 0 ? 'text-no' : 'text-text'} />
      <StatCard label="Focus Time" value={`${totalMinutes}m`} />
      <StatCard label="Productive / Distracted" value={`${productive} / ${unproductive}`} />
    </div>
  );
};

const StatCard = ({ label, value, highlight }: { label: string, value: string | number, highlight?: string }) => (
  <div className="bg-surface p-3 sm:p-4 rounded-lg border border-border shadow-sm text-center">
    <p className="text-[11px] font-semibold text-muted uppercase tracking-wider">{label}</p>
    <p className={`mt-1 text-lg sm:text-xl font-semibold tabular-nums ${highlight || 'text-text'}`}>{value}</p>
  </div>
);

export interface TrendPoint {
  name: string;
  rate: number;
  total: number;
}

interface HistoryChartProps {
  title: string;
  data: TrendPoint[];
}

export const HistoryChart: React.FC<HistoryChartProps> = ({ title, data }) => {
  if (data.length === 0) {
    return <div className="text-center py-8 text-muted text-sm">No history data yet. Start a session!</div>;
  }

  return (
    <div className="h-72 w-full bg-surface p-4 pb-6 rounded-lg border border-border shadow-sm">
      <h3 className="text-sm font-semibold text-text mb-4">{title}</h3>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data}>
          <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={false} />
          <YAxis hide />
          <Tooltip 
            cursor={{fill: '#f1f5f9'}}
            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
          />
          <Bar dataKey="rate" radius={[4, 4, 0, 0]}>
            {data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.rate >= 50 ? '#16A34A' : '#DC2626'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};
